import {
  Breadcrumbs,
  Button,
  Inline,
  Link,
  Notification,
  Skeleton,
  Stack,
  Typography,
  useModal,
} from '@omnipresentgroup/design-system';
import { useXeroConnection } from '@omnipresentgroup/domains';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import { XeroConnectionStatusEnum } from '@omnipresentgroup/types';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { INTEGRATIONS_PATH } from '../../constants';
import {
  XERO_GUIDE_LINK,
  XeroAccountCodeMappingsList,
  XeroDisconnectModal,
  XeroOrganisationSelection,
} from '../../features/xero-integration';
import { useManagerUser } from '../../hooks';
import { INTEGRATIONS_PAGE_TITLE } from './Integrations';

const XERO_CONFIGURATION_TITLE = 'Xero Configuration';
const DISCONNECT_BUTTON_LABEL = 'Disconnect from Xero';
const NOTIFICATION_DESCRIPTION =
  'Categorise every line item in an Omnipresent invoice to a related Xero account, for enhanced financial accuracy.';
const NOTIFICATION_LINK_LABEL = 'View guide';

const SkeletonLoader = () => (
  <Stack gap="4">
    <Skeleton width={200} />
    <Skeleton height={40} width={400} />
  </Stack>
);

export const XeroConfigurationPage = () => {
  const { modalProps } = useModal({});
  const navigate = useNavigate();

  const { companyId } = useManagerUser();
  const {
    data: xeroConnection,
    isLoading: isXeroConnectionLoading,
    refetch,
  } = useXeroConnection({
    clientId: companyId,
  });

  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);

  const PageHeader = () => (
    <Stack gap="0">
      <Inline justify="space-between" minH={40} align="center">
        <Typography as="h2" hideParagraphSpacing size="24" weight="medium">
          {XERO_CONFIGURATION_TITLE}
        </Typography>
        <Button
          variant="secondary"
          onClick={() => setDisconnectModalOpen(true)}
        >
          {DISCONNECT_BUTTON_LABEL}
        </Button>
      </Inline>

      <Typography as="span" hideParagraphSpacing size="16" weight="regular">
        {toSentenceCase(xeroConnection?.xeroOrganisation?.name ?? '')}
      </Typography>
    </Stack>
  );

  /**
   * Redirect to the Integrations page if the user is not connected to Xero
   * Redirect to the Xero Organisation page if the organisation is not defined
   */
  useEffect(() => {
    if (
      xeroConnection?.connectionStatus ===
      XeroConnectionStatusEnum.NOT_CONNECTED
    ) {
      navigate(INTEGRATIONS_PATH);
      return;
    }
  }, [navigate, xeroConnection]);

  const noOrganization = useMemo(
    () =>
      xeroConnection?.connectionStatus === XeroConnectionStatusEnum.CONNECTED &&
      !xeroConnection?.xeroOrganisation,
    [xeroConnection?.connectionStatus, xeroConnection?.xeroOrganisation],
  );

  const ConfigurationContent = () =>
    noOrganization ? (
      <Inline justify="center">
        <XeroOrganisationSelection onOrganizationSelection={() => refetch()} />
      </Inline>
    ) : (
      <>
        <Notification
          id="xero-account-code-mappings-list-notification"
          title={NOTIFICATION_DESCRIPTION}
          link={
            <Link size="14" onClick={() => window.open(XERO_GUIDE_LINK)}>
              {NOTIFICATION_LINK_LABEL}
            </Link>
          }
        />
        <Inline justify="center">
          <XeroAccountCodeMappingsList
            columnVisibility={{
              category: true,
              xeroAccount: true,
              applyAll: true,
            }}
            wrapperProps={{
              w: 900,
            }}
            xeroOrganisation={xeroConnection?.xeroOrganisation?.name}
          />
        </Inline>
      </>
    );

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate('/') },
            {
              id: 'integrations',
              title: INTEGRATIONS_PAGE_TITLE,
              onClick: () => navigate(INTEGRATIONS_PATH),
            },
            {
              id: 'xero-configuration',
              title: XERO_CONFIGURATION_TITLE,
              active: true,
            },
          ]}
        />
      </Stack>
      <PageHeader />
      {isXeroConnectionLoading ? <SkeletonLoader /> : <ConfigurationContent />}
      {disconnectModalOpen && (
        <XeroDisconnectModal
          modalProps={{
            ...modalProps,
            open: disconnectModalOpen,
            onOpenChange: setDisconnectModalOpen,
          }}
          handleClose={() => setDisconnectModalOpen(false)}
          clientId={companyId}
        />
      )}
    </>
  );
};
